import type { PartFittingView, TruckPartsListQuery } from '@chukua/common';
import type { TableColumnsType } from 'antd';
import { Link } from 'react-router';
import { truckTabPath } from '../../../../fleet/features/trucks/trucks.paths';
import { unitPath } from '../../../../fleet/features/units/units.paths';
import type { ListQueryControls } from '../../../../../shared/hooks/use-list-query';
import { formatMoney } from '../../../../../shared/policies/format-money';
import { partTypePath } from '../../part-types/part-types.paths';
import { formatFittedDate, formatOdometer, workOrderLabel } from '../policies/part-labels';

interface BuildPartColumnsArgs {
  list: Pick<ListQueryControls<TruckPartsListQuery>, 'sortOrderFor'>;
  /** Off on a truck's own tab, where the truck is the page. */
  showTruck: boolean;
}

/** Each sortable column's `key` is its sort field in `partsRegisterListQuerySchema` / `truckPartsListQuerySchema`; the filters are in the toolbar, because a column filter shares its column's key with its sort. */
export function buildPartColumns({ list, showTruck }: BuildPartColumnsArgs): TableColumnsType<PartFittingView> {
  const columns: TableColumnsType<PartFittingView> = [
    { title: 'Fitted', dataIndex: 'fittedAt', key: 'fittedAt', sorter: true, sortOrder: list.sortOrderFor('fittedAt'), render: formatFittedDate },
  ];
  if (showTruck) {
    columns.push({
      title: 'Truck',
      key: 'truckId',
      render: (_, row) => (
        <Link to={row.truckRegistration ? unitPath(row.truckRegistration, 'parts') : truckTabPath(row.truckId, 'parts')}>
          {row.truckRegistration ?? 'Truck'}
        </Link>
      ),
    });
  }
  columns.push(
    {
      title: 'Part type',
      dataIndex: 'partTypeName',
      key: 'partTypeName',
      sorter: true,
      sortOrder: list.sortOrderFor('partTypeName'),
      render: (name: string | null, row) =>
        row.partTypeId ? <Link to={partTypePath(row.partTypeId)}>{name}</Link> : <span className="text-muted">Unclassified</span>,
    },
    { title: 'Part', dataIndex: 'part', key: 'part' },
    { title: 'Part no.', dataIndex: 'partNumber', key: 'partNumber', render: (value: string | null) => value ?? '—' },
    { title: 'Serial', dataIndex: 'serial', key: 'serial', render: (value: string | null) => (value ? <span className="font-mono">{value}</span> : '—') },
    { title: 'Qty', dataIndex: 'quantity', key: 'quantity' },
    { title: 'Unit cost', dataIndex: 'unitCost', key: 'unitCost', sorter: true, sortOrder: list.sortOrderFor('unitCost'), render: formatMoney },
    { title: 'Supplier', dataIndex: 'supplierName', key: 'supplierName', render: (value: string | null) => value ?? '—' },
    { title: 'Odometer', dataIndex: 'odometerKm', key: 'odometerKm', sorter: true, sortOrder: list.sortOrderFor('odometerKm'), render: formatOdometer },
    {
      title: 'Work order',
      dataIndex: 'workOrderNumber',
      key: 'workOrderNumber',
      sorter: true,
      sortOrder: list.sortOrderFor('workOrderNumber'),
      render: (_, row) => workOrderLabel(row),
    },
  );
  return columns;
}
